import { apiClient } from './apiService'

export interface Label {
  id: string
  name: string
  value: string
  count: number
}

export interface LabelStats {
  totalLabels: number
  totalIssues: number
  issuesWithLabels: number
  issuesWithoutLabels: number
  averageLabelsPerIssue: string
  mostUsedLabels: Array<{
    label: string
    count: number
  }>
}

export interface LabelStatsResponse {
  success: boolean
  data: LabelStats
  message: string
  error?: string
}

export interface LabelResponse {
  success: boolean
  data: Label[]
  total: number
  message: string
  cached?: boolean
  searchTerm?: string
  error?: string
}

export interface LabelIssue {
  key: string
  summary: string
  status: string
  labels: string[]
}

export interface LabelIssuesResponse {
  success: boolean
  data: LabelIssue[]
  total: number
  label: string
  message: string
  error?: string
}

class LabelService {
  /**
   * Get all labels
   */
  async getAllLabels(jql?: string): Promise<LabelResponse> {
    try {
      const params = jql ? { jql } : {}
      const response = await apiClient.get('/labels', { params })
      return response.data
    } catch (error: any) {
      console.error('Error fetching labels:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Failed to fetch labels',
        error: error.response?.data?.message || error.message
      }
    }
  }

  /**
   * Search labels by name
   */
  async searchLabels(searchTerm: string, jql?: string): Promise<LabelResponse> {
    try {
      const params: any = { q: searchTerm }
      if (jql) params.jql = jql
      
      const response = await apiClient.get('/labels/search', { params })
      return response.data
    } catch (error: any) {
      console.error('Error searching labels:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Failed to search labels',
        error: error.response?.data?.message || error.message
      }
    }
  }

  /**
   * Get label statistics
   */
  async getLabelStats(): Promise<LabelStatsResponse> {
    try {
      const response = await apiClient.get('/labels/stats')
      return response.data
    } catch (error: any) {
      console.error('Error fetching label stats:', error)
      return {
        success: false,
        data: {
          totalLabels: 0,
          totalIssues: 0,
          issuesWithLabels: 0,
          issuesWithoutLabels: 0,
          averageLabelsPerIssue: '0',
          mostUsedLabels: []
        },
        message: 'Failed to fetch label statistics',
        error: error.response?.data?.message || error.message
      }
    }
  }

  /**
   * Get issues by label
   */
  async getIssuesByLabel(label: string): Promise<LabelIssuesResponse> {
    try {
      const response = await apiClient.get(`/labels/${encodeURIComponent(label)}/issues`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching issues by label:', error)
      return {
        success: false,
        data: [],
        total: 0,
        label,
        message: 'Failed to fetch issues by label',
        error: error.response?.data?.message || error.message
      }
    }
  }

  /**
   * Get labels for a specific issue
   */
  async getIssueLabels(issueKey: string): Promise<{ success: boolean; data: string[]; message: string; error?: string }> {
    try {
      const response = await apiClient.get(`/labels/issue/${issueKey}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching issue labels:', error)
      return {
        success: false,
        data: [],
        message: 'Failed to fetch issue labels',
        error: error.response?.data?.message || error.message
      }
    }
  }

  /**
   * Clear labels cache
   */
  async clearCache(): Promise<{ success: boolean; message: string; error?: string }> {
    try {
      const response = await apiClient.post('/labels/cache/clear')
      return response.data
    } catch (error: any) {
      console.error('Error clearing labels cache:', error)
      return {
        success: false,
        message: 'Failed to clear labels cache',
        error: error.response?.data?.message || error.message
      }
    }
  }
}

export const labelService = new LabelService()
export default labelService
